import React, { useCallback, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query'
import { Page } from '@progress/kendo-react-dropdowns'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import {
  PspWdCheckPaymentOptionsDto,
  WalletPaymentProviderFilterKey,
  WalletPaymentProviderKey,
} from 'api/Wallet/Types/FullPaymentProviderDto'
import { Loading } from 'components/Loading'
import { calculatePageIndex } from 'features/DataGrid/helpers'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOneWithFetchingUpdate } from 'utils/api/useFetchOneWithFetchingUpdate'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { links } from 'utils/featureToggles'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { buildQuery, FilterLogic, isFilterDescriptor } from 'utils/QueryHelpers'

import ClientAreaAuditLogModal from '../../../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import MoreButton from '../../../../../components/MoreButton/MoreButton'
import { paymentControlsPSPCheckStateColumn } from './paymentControlsPSPCheckStateColumn'

const PaymentControlsPSPCheckStatePage = (): JSX.Element => {
  const walletsApi = useApiClient(WalletsApiClient)

  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })
  const [paginationState, setPaginationState] = useState<Page>(getPaginationInitialState())
  const [auditLogModal, setAuditLogModal] = useState<{
    visible: boolean
    data?: PspWdCheckPaymentOptionsDto
  }>({
    visible: false,
  })
  const { callback } = useCallbackWithForceRefresh(
    () =>
      walletsApi.getPaymentProviders({
        search: getSearchQuery(),
        pageIndex: calculatePageIndex(paginationState.skip, paginationState.take),
        pageSize: paginationState.take,
      }),
    [paginationState] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const { data, isLoading } = useFetchOneWithFetchingUpdate(callback)

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!isLoading) {
        setPaginationState((page) => ({ ...page, skip: 0 }))
      }
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [filter?.filters]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = async (event: GridFilterChangeEvent) => {
    setFilter(event.filter)
  }

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPaginationState(event.page)
  }

  const getSearchQuery = useCallback(
    () => {
      const filterSearchQuery = normalizePaymentControlsPSPCheckStateSearchQuery(filter)
      return (
        buildQuery({
          logic: FilterLogic.AND,
          filters: filterSearchQuery.filters,
        }) ?? null
      )
    },
    [filter?.filters] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const setAuditLog = (pspWdCheckPaymentOptionsDto: PspWdCheckPaymentOptionsDto) => {
    setAuditLogModal({ data: pspWdCheckPaymentOptionsDto, visible: true })
  }

  return (
    <React.Fragment>
      {auditLogModal.visible && auditLogModal.data && (
        <ClientAreaAuditLogModal
          isVisible={auditLogModal.visible}
          onClose={() => setAuditLogModal({ visible: false })}
          pspMethodId={auditLogModal.data.paymentProvider.id}
        />
      )}

      <Breadcrumbs
        crumbs={[
          { title: 'PaymentControls', link: '#' },
          { title: 'PSP Check State', link: links.administrationPaymentControlsPSPCheckState.url },
        ]}
      />
      <Loading isLoading={isLoading}>
        {data && (
          <Grid
            filterable
            resizable
            data={data.items}
            filter={filter}
            take={paginationState.take}
            skip={paginationState.skip}
            total={data.totalResults}
            onPageChange={handlePageChange}
            onFilterChange={handleFilterChange}
            pageable={pageableSettings}
          >
            {paymentControlsPSPCheckStateColumn
              .filter((c) => c.show !== false)
              .map((column, idx) => (
                <Column key={idx} {...column} />
              ))}
            <Column
              width={50}
              title=''
              cell={(gridCellProps: GridCellProps) => (
                <td>
                  <MoreButton
                    items={[
                      {
                        name: 'Audit Log',
                        onClick: () => setAuditLog(gridCellProps.dataItem),
                      },
                    ]}
                  />
                </td>
              )}
            />
          </Grid>
        )}
      </Loading>
    </React.Fragment>
  )
}

const normalizePaymentControlsPSPCheckStateSearchQuery = (
  filterDescriptor: CompositeFilterDescriptor
) => {
  const filters = (filterDescriptor?.filters ?? [])
    .filter((filterItem: FilterDescriptor | CompositeFilterDescriptor) => {
      if (isFilterDescriptor(filterItem)) {
        return !!filterItem.value
      }
      return false
    })
    .map((filterItem) => {
      if (isFilterDescriptor(filterItem)) {
        if (filterItem.field === WalletPaymentProviderKey.name) {
          return {
            ...filterItem,
            field: WalletPaymentProviderFilterKey.name,
            operator: 'like',
            value: filterItem?.value,
          }
        }
        if (filterItem.field === WalletPaymentProviderFilterKey.CheckStateIb) {
          return {
            ...filterItem,
            field: WalletPaymentProviderFilterKey.CheckStateIb,
            operator: '==',
            value: filterItem?.value,
          }
        }
        if (filterItem.field === WalletPaymentProviderFilterKey.CheckStateFx) {
          return {
            ...filterItem,
            field: WalletPaymentProviderFilterKey.CheckStateFx,
            operator: '==',
            value: filterItem?.value,
          }
        }
      }
      return filterItem
    })

  return { ...filterDescriptor, filters }
}

export default PaymentControlsPSPCheckStatePage
